import { useState, type FormEvent } from "react";
import s from "./Planning.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { usePlan } from "../../api/queries";
import { hasPermission, PERMISSION, type PlanQuery } from "../../api/dataSource";
import {
  blockedBy,
  isNoOverlap,
  outcome,
  OUTCOME_LABEL,
  sideOutcome,
  windowHours,
  type PlanAnswer,
  type PlanSide,
} from "../../domain/planning";
import { Button, Card, EmptyState, ErrorState, Field, Input, Skeleton } from "../../design/components";
import { formatDateTime } from "../../lib/format";
import { t } from "../../i18n";

/** Default search span: long enough to find a night window, short enough that
 *  the platform will accept it — it refuses a search it cannot bound. */
const DEFAULT_DAYS = 5;

const isoLocal = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

/**
 * When to do the work.
 *
 * This is the screen the whole platform argues towards: not what is wrong with
 * the pavement, not what it will cost, but the hours to book the crew for. It
 * is one question and one sentence of answer.
 *
 * THE THING TO GET RIGHT is that there are three outcomes and only one of them
 * is a plan. A window found, a window genuinely absent, and a question that
 * could not be answered because half the data is missing — the third is the
 * one a screen loses, because it also has `found: false`. It is drawn as an
 * incomplete box rather than a red one: nothing is blocked, nobody has looked.
 */
export function PlanningPage() {
  const { site, principal } = useSession();
  /* BOTH keys, not either. The platform requires both because the answer is
     only as trustworthy as its weaker half, and somebody holding one would
     otherwise be handed a joint verdict built on data they may not see. */
  const may = hasPermission(principal, PERMISSION.capacityRead) && hasPermission(principal, PERMISSION.weatherRead);

  const now = new Date();
  const later = new Date(now.getTime() + DEFAULT_DAYS * 86400000);
  const [form, setForm] = useState({
    positionCode: "",
    category: "",
    hoursNeeded: "4",
    searchFrom: isoLocal(now),
    searchTo: isoLocal(later),
  });
  const [asked, setAsked] = useState<PlanQuery | null>(null);
  const plan = usePlan(asked);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const ready = form.positionCode.trim() !== "" && form.category.trim() !== "" && Number(form.hoursNeeded) > 0;

  const ask = (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setAsked({
      siteId: site.id,
      positionCode: form.positionCode.trim(),
      category: form.category.trim(),
      hoursNeeded: Number(form.hoursNeeded),
      searchFrom: new Date(form.searchFrom).toISOString(),
      searchTo: new Date(form.searchTo).toISOString(),
    });
  };

  return (
    <Page eyebrow="Deep thinking" title="When to do the work">
      <Card
        title="The job"
        sub="Traffic and weather are answered separately and joined here. Both must be answerable, or there is no plan."
      >
        <form className={s.form} onSubmit={ask}>
          <Field label="Position">
            <Input
              value={form.positionCode}
              onChange={set("positionCode")}
              placeholder={t("e.g. A12")}
              aria-label={t("Position")}
            />
          </Field>
          <Field label="Work category">
            <Input
              value={form.category}
              onChange={set("category")}
              placeholder={t("e.g. crack-seal")}
              aria-label={t("Work category")}
            />
          </Field>
          <Field label="Hours needed">
            <Input
              type="number"
              min="0.5"
              step="0.5"
              value={form.hoursNeeded}
              onChange={set("hoursNeeded")}
              aria-label={t("Hours needed")}
            />
          </Field>
          <Field label="Search from">
            <Input
              type="datetime-local"
              value={form.searchFrom}
              onChange={set("searchFrom")}
              aria-label={t("Search from")}
            />
          </Field>
          <Field label="Search to">
            <Input type="datetime-local" value={form.searchTo} onChange={set("searchTo")} aria-label={t("Search to")} />
          </Field>
          <Button type="submit" variant="primary" disabled={!ready || !may}>
            {t("Find a window")}
          </Button>
        </form>
        {!may && (
          <p className={s.reason} style={{ marginTop: 10 }}>
            {t("This needs both")} <code>capacity:read</code> {t("and")} <code>weather:read</code>
            {t(
              ". The platform requires both deliberately — a joint verdict is only as trustworthy as its weaker half.",
            )}
          </p>
        )}
      </Card>

      {asked && plan.isPending && <Skeleton rows={5} />}
      {asked && plan.isError && <ErrorState error={plan.error} retry={() => plan.refetch()} />}
      {!asked && (
        <EmptyState>
          {t(
            "Name a position, the work and how long it takes. The answer is the hours to book a crew for — or why there are none.",
          )}
        </EmptyState>
      )}
      {plan.data && <Answer answer={plan.data} />}
    </Page>
  );
}

function Answer({ answer }: { answer: PlanAnswer }) {
  const kind = outcome(answer);
  const blocked = blockedBy(answer);

  return (
    <>
      <div
        data-testid="verdict"
        className={`${s.verdict} ${kind === "found" ? s.found : kind === "unknown" ? s.unknown : s.none}`}
      >
        <div className={s.outcome}>{t(OUTCOME_LABEL[kind])}</div>
        {kind === "found" && answer.earliest ? (
          <>
            <div className={s.headline}>
              {t("Do it")} <span className={s.when}>{formatDateTime(answer.earliest.from)}</span> {t("to")}{" "}
              <span className={s.when}>{formatDateTime(answer.earliest.to)}</span>
            </div>
            <div className={s.reason}>
              {t("Clear of traffic and within the weather limits for “{category}”.", { category: answer.category })}
              {answer.windowCount && answer.windowCount > 1
                ? ` ${t("{n} windows qualify; this is the earliest.", { n: answer.windowCount })}`
                : ""}
            </div>
          </>
        ) : (
          <div className={s.headline}>
            {kind === "unknown"
              ? t("This is not a plan — half of it could not be checked")
              : isNoOverlap(answer)
                ? t("Both constraints are clear at different times, and never together")
                : blocked.length === 2
                  ? t("No window — {a} and {b}", { a: t(blocked[0]), b: t(blocked[1]) })
                  : blocked.length === 1
                    ? t("No window — {a}", { a: t(blocked[0]) })
                    : t("No window")}
          </div>
        )}
        {answer.whyNot && <div className={s.reason}>{answer.whyNot}</div>}
        {answer.caveat && <div className={s.caveat}>{answer.caveat}</div>}
      </div>

      <Card title="What each half said" sub="The join is only as good as its weaker side, so both are shown.">
        <div className={s.halves}>
          <Half
            name={t("Traffic")}
            side={answer.traffic}
            hint={t("Can the position be closed without stranding an aircraft")}
          />
          <Half name={t("Weather")} side={answer.weather} hint={t("Can the work physically and legally be done")} />
        </div>
      </Card>
    </>
  );
}

function Half({ name, side, hint }: { name: string; side: PlanSide; hint: string }) {
  const kind = sideOutcome(side);
  return (
    <div className={s.half}>
      <div className={s.halfHead}>
        <span className={s.halfName}>{name}</span>
        <span className={s.outcome}>{t(OUTCOME_LABEL[kind])}</span>
      </div>
      <div className={s.reason} style={{ fontSize: 13 }}>
        {hint}
      </div>

      {side.windows.length > 0 && (
        <div className={s.windows}>
          {side.windows.map((w) => {
            const hrs = windowHours(w);
            return (
              <div className={s.window} key={`${w.from}-${w.to}`}>
                <span>
                  {formatDateTime(w.from)} → {formatDateTime(w.to)}
                </span>
                {/* null, not 0: an unparseable span is a fact about the data */}
                <span className={s.dur}>{hrs == null ? "—" : t("{n} h", { n: hrs.toFixed(1) })}</span>
              </div>
            );
          })}
        </div>
      )}

      {side.whyNot && <div className={s.reason}>{side.whyNot}</div>}

      {side.blocking && side.blocking.length > 0 && (
        <div className={s.blockers}>
          {side.blocking.map(([reason, count]) => (
            <div className={s.blocker} key={reason}>
              <span>{reason}</span>
              <span>{t("{n} h", { n: count })}</span>
            </div>
          ))}
        </div>
      )}

      {/* Which records the answer came from. A plan made on a forecast is a
          reasonable plan; a plan made on a forecast recorded as a measurement
          is one nobody can audit after it goes wrong. */}
      {side.assessedFrom && side.assessedFrom.length > 0 && (
        <div className={s.assessed}>{t("Assessed from {sources}.", { sources: side.assessedFrom.join(", ") })}</div>
      )}
    </div>
  );
}
